/**
 * Integrytas Group — DateInput
 *
 * A date field that is reliably driven by TYPING, with a native calendar picker
 * kept for mouse users. The previous version wrapped a bare `<input type="date">`
 * and tried to rescue a two-digit year on blur — but a native date control never
 * commits a partially-typed year (typing "26" leaves the value empty until four
 * digits are entered), so the rescue had nothing to act on and short-year entry
 * silently failed. This version reads a real TEXT input the user can type a whole
 * date into (e.g. "9/10/26"), parses it in the active locale's day/month/year
 * order, and coerces a 1–2 digit year to the current millennium ("26" → 2026),
 * so the common "the millennium wasn't entered" mistake just works.
 *
 * API is unchanged: `value` is ISO `yyyy-mm-dd` (or ''), `onChange` receives the
 * (normalised) ISO value, and all other input props (disabled, className, min,
 * max, id, aria-*, …) pass straight through. `min`/`max` are honoured both by the
 * native picker and by the typed-entry validation.
 *
 * The picker sits under a small calendar affordance on the right; the field's
 * day/month/year order follows the active locale (`document.documentElement.lang`,
 * kept in sync with the CSM locale, §11), exactly like the rest of the app's
 * date rendering.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import type { InputHTMLAttributes } from 'react';

export interface DateInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'value' | 'onChange'> {
  value: string;                       // ISO yyyy-mm-dd (or '')
  onChange: (value: string) => void;   // receives the (normalised) ISO value
}

type Part = 'd' | 'm' | 'y';

/** Resolve the active locale's field order (e.g. en-US → m,d,y; en-GB → d,m,y). */
function localeDateOrder(): Part[] {
  const lang = (typeof document !== 'undefined' && document.documentElement.lang) || undefined;
  try {
    const parts = new Intl.DateTimeFormat(lang, { year: 'numeric', month: '2-digit', day: '2-digit' })
      .formatToParts(new Date(Date.UTC(2000, 0, 2)));
    const order = parts
      .filter(p => p.type === 'day' || p.type === 'month' || p.type === 'year')
      .map(p => p.type[0] as Part);
    return order.length === 3 ? order : ['d', 'm', 'y'];
  } catch {
    return ['d', 'm', 'y'];
  }
}

/** Placeholder like "dd/mm/yyyy" in the active order. */
function placeholderFor(order: Part[]): string {
  return order.map(p => (p === 'y' ? 'yyyy' : p === 'm' ? 'mm' : 'dd')).join('/');
}

/** ISO → display text in the active order (e.g. "2026-09-10" → "10/09/2026"). */
function isoToText(iso: string, order: Part[]): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return '';
  const map: Record<Part, string> = { y: m[1], m: m[2], d: m[3] };
  return order.map(p => map[p]).join('/');
}

/**
 * Normalise a native/ISO date value: a year of 1..999 is assumed to be this
 * millennium (add 2000). Kept for backward compatibility with any caller that
 * imported it; the typed-entry path below applies the same coercion by digit
 * count so a two-digit "26" also becomes 2026.
 */
export function normalizeMillenniumYear(iso: string): string {
  const m = /^(\d{1,4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return iso;
  let year = Number(m[1]);
  if (year >= 1 && year <= 999) year += 2000;
  return `${String(year).padStart(4, '0')}-${m[2]}-${m[3]}`;
}

/**
 * Parse a freely-typed date in the given field order into ISO, coercing a
 * short (1–2 digit) year to the current millennium. Returns '' for empty input
 * (a legitimate clear) and null for anything that is not a real calendar date.
 */
export function parseTypedDate(text: string, order: Part[]): string | null {
  const cleaned = text.trim();
  if (!cleaned) return '';
  const nums = cleaned.split(/[^\d]+/).filter(Boolean);
  if (nums.length !== 3) return null;
  const map = {} as Record<Part, string>;
  order.forEach((p, i) => { map[p] = nums[i]; });

  let year = Number(map.y);
  const month = Number(map.m);
  const day = Number(map.d);
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return null;
  // "The millennium wasn't entered": 1–2 digit years fold into the current one;
  // 3-digit years keep parity with the historic normalizeMillenniumYear rule.
  if (map.y.length <= 2 || (year >= 1 && year <= 999)) year += 2000;

  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  const iso = `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  // Reject impossible calendar dates (e.g. 31/02) by round-tripping through Date.
  const dt = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(dt.getTime()) || dt.getUTCMonth() + 1 !== month || dt.getUTCDate() !== day) return null;
  return iso;
}

export function DateInput({ value, onChange, onBlur, className, style, disabled, min, max, placeholder, ...rest }: DateInputProps) {
  const order = useMemo(() => localeDateOrder(), []);
  const [text, setText] = useState(() => isoToText(value, order));
  const editingRef = useRef(false);

  // Keep the displayed text in sync with the ISO value whenever it changes from
  // the outside (reset, picker, another field) and the user is not mid-edit.
  useEffect(() => {
    if (!editingRef.current) setText(isoToText(value, order));
  }, [value, order]);

  const minIso = typeof min === 'string' ? min : undefined;
  const maxIso = typeof max === 'string' ? max : undefined;
  const withinRange = (iso: string) =>
    (!minIso || iso >= minIso) && (!maxIso || iso <= maxIso);

  // Commit typed text: emit ISO for a valid in-range date, '' for a clear, and
  // for anything invalid revert the text to the last good ISO (no silent bad value).
  const commit = () => {
    editingRef.current = false;
    const parsed = parseTypedDate(text, order);
    if (parsed === '') { setText(''); if (value !== '') onChange(''); return; }
    if (parsed && withinRange(parsed)) {
      setText(isoToText(parsed, order));
      if (parsed !== value) onChange(parsed);
      return;
    }
    setText(isoToText(value, order)); // discard unparseable / out-of-range entry
  };

  return (
    <span className="relative inline-flex items-center">
      <input
        {...rest}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        disabled={disabled}
        className={className}
        style={{ ...style, paddingRight: '1.9rem' }}
        placeholder={placeholder ?? placeholderFor(order)}
        value={text}
        onFocus={() => { editingRef.current = true; }}
        onChange={e => { editingRef.current = true; setText(e.target.value); }}
        onKeyDown={e => { if (e.key === 'Enter') commit(); }}
        onBlur={e => { commit(); onBlur?.(e); }}
      />
      {/* Native picker for mouse users: a transparent date control laid over a
          calendar glyph on the right. Picking a date emits ISO directly; it is
          out of the tab order so keyboard users type into the text field. */}
      <span aria-hidden className="pointer-events-none absolute inset-y-0 right-0 flex w-7 items-center justify-center text-csm-ink-soft">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
        </svg>
      </span>
      <input
        type="date"
        tabIndex={-1}
        aria-hidden
        disabled={disabled}
        min={minIso}
        max={maxIso}
        value={value}
        onChange={e => { const v = e.target.value; setText(isoToText(v, order)); if (v !== value) onChange(v); }}
        className="absolute inset-y-0 right-0 w-7 cursor-pointer opacity-0"
      />
    </span>
  );
}
